/**
 * 애플리케이션 shell (계획서 §13).
 *
 * 로그인 후 운전 화면(§13.1~§13.5)을 보여 준다.
 */

import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { fetchReadiness } from "@/api/client";
import { useAuthStore } from "@/features/auth/authStore";
import { LoginScreen } from "@/features/auth/LoginScreen";
import { OperationScreen } from "@/features/run/OperationScreen";
import type { HealthReadyResponse } from "@/types/api";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 5_000 },
  },
});

const READINESS_LABEL: Record<HealthReadyResponse["status"], string> = {
  ready: "정상",
  degraded: "일부 저하",
  unready: "준비되지 않음",
};

function BackendStatus(): React.JSX.Element {
  const { data, isPending, isError } = useQuery({
    queryKey: ["health", "ready"],
    queryFn: fetchReadiness,
    refetchInterval: 15_000,
  });

  if (isPending) return <span className="status-chip">backend 확인 중…</span>;
  if (isError || !data) return <span className="status-chip status-error">backend 연결 실패</span>;

  const tone = data.status === "ready" ? "status-ok" : "status-warn";
  const detail = Object.entries(data.checks)
    .map(([name, check]) => `${name}: ${check.status}`)
    .join(" · ");

  return (
    <span className={`status-chip ${tone}`} title={detail}>
      backend {READINESS_LABEL[data.status]}
    </span>
  );
}

function AuthenticatedShell(): React.JSX.Element {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  return (
    <div className="app-shell">
      <header className="app-header">
        <h1>에너지 효율성 Multi-Agent</h1>
        <div className="app-header-right">
          <BackendStatus />
          <span className="user-chip">{user?.username ?? ""}</span>
          <button
            type="button"
            onClick={() => {
              void logout();
            }}
          >
            로그아웃
          </button>
        </div>
      </header>

      <OperationScreen />
    </div>
  );
}

function Router(): React.JSX.Element {
  const phase = useAuthStore((s) => s.phase);
  const restore = useAuthStore((s) => s.restore);

  useEffect(() => {
    void restore();
  }, [restore]);

  if (phase === "unknown") return <div className="app-loading">불러오는 중…</div>;
  if (phase === "anonymous") return <LoginScreen />;
  return <AuthenticatedShell />;
}

export function App(): React.JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <Router />
    </QueryClientProvider>
  );
}
